import { inject, Injectable } from '@angular/core'

import { firstValueFrom, takeUntil } from 'rxjs'

import { Group, Resource } from '@ti/contracts'
import { BaseCachedStore } from '@ti/core/models/cached-store'

import { GroupsManager } from './groups.manager'

@Injectable({ providedIn: 'root' })
export class GroupsStore extends BaseCachedStore<Group> {
  protected readonly CACHE_KEY: string = 'TI_GROUPS'
  protected readonly CHECK_RESOURCE_ACCESS = Resource.Group
  protected readonly MAX_ITEMS = 1000

  protected readonly groupsManager = inject(GroupsManager)

  public async getById(groupId: string) {
    await this.ensureIsFresh() // Wait until data is fresh

    const groups = await firstValueFrom(this.store.data$)
    return groups?.find((group) => group.id === groupId)
  }

  protected override loadAll() {
    return this.api.fleet.groups.list({ pageSize: this.MAX_ITEMS })
  }

  protected override loadById(ids: string[]) {
    return this.api.fleet.groups.list({
      filter: { id: { in: ids } },
      pageSize: this.MAX_ITEMS,
    })
  }

  protected override loadSnapshot() {
    return this.api.fleet.groups.list({
      select: ['id', 'updatedAt'],
      pageSize: 10000,
    }) as Promise<{ id: string; updatedAt: Date }[]>
  }

  protected override async init() {
    await super.init()

    // Refresh store after group is updated
    this.groupsManager.updated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ids) => (ids?.length ? this.refreshById(ids) : this.refreshAll()))
  }
}
